import { removeEmptyEntries } from "@lib-shared/lib";
import { appSettings } from "core/config";
import { logApiError } from "core/lib";
import { EncryptedCustomerResponse } from "core/services/cef/permission.service.types";
import { Address, Agreement } from "core/services/cef/types";
import { ModalAlert } from "components/common-ui";
import { BusinessAccount } from "./types";

const getAddress = (addresses: Address[]) => {
    if (!addresses.length) {
        return [];
    }

    const { street, suburb, province } = addresses[0];
    return [street, suburb, province];
}

const getAddresses = (response: EncryptedCustomerResponse) => {
    //Note: Some accounts don't have an address setup
    if (!!response?.customer.address) {
        return response.customer.address;
    }

    if (response.agreements?.length && !!response.agreements[0]?.address) {
        return response.agreements[0]?.address;
    }

    return [];
}

const getMainAccountDetails = (response: EncryptedCustomerResponse) => {
    const useAutoSelect = response.agreements.length >= appSettings.autoSelectAccounts;

    const {
        firstName,
        lastName,
        customerNumber,
        encryptedCustomerNumber,
        email,
        encryptedEmail
    } = response?.customer;

    const contactName = `${firstName ?? ""} ${lastName ?? ""}`.trim();

    const {
        countryCode,
        cellNumber,
        encryptedCellNumber,
        workNumber,
        encryptedWorkNumber
    } = response?.customer?.primaryContactDetails[0];

    let contactNumber = (cellNumber ?? workNumber ?? "").trim();

    if (contactNumber && countryCode) {
        contactNumber = `+${countryCode} ${contactNumber}`;
    }

    const addresses = getAddresses(response);
    const address = getAddress(addresses);

    return {
        address,
        customerNumber,
        contactName,
        contactNumber,
        contactEmail: email,
        isSelected: useAutoSelect,
        encrypted: {
            email: encryptedEmail,
            customerNumber: encryptedCustomerNumber,
            cell: encryptedCellNumber ?? encryptedWorkNumber ?? ""
        }
    };
}

export const getAccounts = (response: EncryptedCustomerResponse) => {
    if (response === null) {
        throw new Error("There is no account data");
    }

    if (!response?.agreements?.length) {
        throw new Error("Customer has no accounts");
    }

    const list = new Array<BusinessAccount>();

    const mainAccountDetails = getMainAccountDetails(response);

    const getAccount = (account: Agreement): BusinessAccount => {
        return {
            ...mainAccountDetails,
            id: account.id,
            name: account.friendlyName,
        };
    }

    response.agreements.forEach(x => list.push(getAccount(x)));

    return list;
}

export const getAndLogError = (ex: any, defaultMessage?: string, logError = true) => {
    if (logError) {
        logApiError(ex);
    }

    const messages = [];
    const errorDesc = ex.response?.data?.errorResponse?.errorDescription ?? ex.response?.data?.errorDescription;

    if (errorDesc != null && !errorDesc.includes("[IS")) {
        messages.push(errorDesc);
    }

    messages.push(defaultMessage);
    messages.push(ex?.message);
    messages.push(ex?.exception);

    const message = removeEmptyEntries(messages)[0];

    return message;
}

export const showErrorToUser = (ex: any, defaultMessage: string, logError = true) => {
    const message = getAndLogError(ex, defaultMessage, logError);

    ModalAlert.error(message);
}

export const gotoHomepage = (e: any) => {
    if (e != null) {
        e.preventDefault();
    }

    window.location.href = appSettings.urls.homePage;
}

export const handleFailure = () => ModalAlert.error("Sorry there has been an error");